import { useState } from "react";
import { api } from "../../../lib/api";
import { toast } from "sonner";
import { Mail, X, Send } from "lucide-react";

export function DepositBalanceEmailPrompt({ b, reload }) {
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const required = b.deposit_balance_email_required && !b.deposit_balance_email_sent_at;
  if (b.deposit_balance_email_sent_at) {
    return (
      <p className="text-[11px] text-[#1F6B3B] mt-1.5" data-testid="deposit-balance-email-sent-note">
        ✓ Balance-due email sent {new Date(b.deposit_balance_email_sent_at).toLocaleString("en-GB")}
        {b.deposit_balance_email_by ? ` by ${b.deposit_balance_email_by}` : ""}.
      </p>
    );
  }
  if (!required) return null;

  const openModal = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/admin/booking/${b.reference}/deposit-balance-email`);
      setTo(r.data.to);
      setSubject(r.data.subject);
      setBody(r.data.body);
      setOpen(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load the email preview"); }
    setBusy(false);
  };

  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/booking/${b.reference}/deposit-balance-email/send`, { subject, body });
      toast.success(`Balance-due email sent to ${r.data.to}`);
      setOpen(false);
      reload();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" && !d.trim().startsWith("<")
        ? d : "Could not send via Gmail — the mailbox connection may need reconnecting in Settings → Connected email.");
    }
    setBusy(false);
  };

  return (
    <>
      <button data-testid="deposit-balance-email-required" disabled={busy} onClick={openModal}
        className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest px-3 py-1.5 bg-[#FCE3D6] border border-[#A87E28] text-[#A87E28] hover:bg-[#A87E28] hover:text-white transition-colors disabled:opacity-60">
        <Mail size={13} /> Email required — deposit balance due · Preview &amp; send
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="deposit-balance-email-modal">
          <div className="bg-white w-full max-w-2xl border border-[#E5E7EB] shadow-xl">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E7EB]">
              <div>
                <div className="label-eyebrow">Balance-due email — review before sending</div>
                <div className="text-xs text-[#6B7280] mt-0.5">To: <b className="text-[#12263A]">{to}</b> · A payment summary box + "Sign in &amp; pay" button is added automatically below your text.</div>
              </div>
              <button data-testid="dbe-close" onClick={() => setOpen(false)} className="text-[#6B7280] hover:text-[#12263A]"><X size={18} /></button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="text-[11px] text-[#6B7280] uppercase tracking-wider">Subject</label>
                <input data-testid="dbe-subject" value={subject} onChange={e => setSubject(e.target.value)}
                  className="w-full border border-[#E5E7EB] px-2 py-1.5 text-sm" />
              </div>
              <div>
                <label className="text-[11px] text-[#6B7280] uppercase tracking-wider">Message</label>
                <textarea data-testid="dbe-body" rows={9} value={body} onChange={e => setBody(e.target.value)}
                  className="w-full border border-[#E5E7EB] px-2 py-1.5 text-sm" />
              </div>
            </div>
            <div className="flex justify-end gap-2 px-4 py-3 border-t border-[#E5E7EB]">
              <button data-testid="dbe-cancel" onClick={() => setOpen(false)} className="de-btn-secondary text-xs">Cancel</button>
              <button data-testid="dbe-send" disabled={busy || !subject.trim() || !body.trim()} onClick={send}
                className="de-btn-primary text-xs inline-flex items-center gap-1.5 disabled:opacity-60">
                <Send size={13} /> {busy ? "Sending…" : "Send email"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
